import {
  AudienceTagCondition,
  AUDIENCE_TAG_MISSING_VALUE,
  normalizeAudienceTagValue,
  PopulationFilter,
} from '@digital-twins/shared-types'

interface IndustryLabel {
  token: string
  name: string
}

function conditionToFilterNode(condition: AudienceTagCondition): NonNullable<
  PopulationFilter['conditions']
>[number] {
  const comparable = new Set<string>()
  let includeEmpty = false
  for (const value of condition.values) {
    const normalized = normalizeAudienceTagValue(value)
    if (normalized === AUDIENCE_TAG_MISSING_VALUE) {
      includeEmpty = true
      continue
    }
    comparable.add(normalized)
    const raw = String(value).trim().toLowerCase()
    if (raw && raw !== normalized) {
      comparable.add(raw)
    }
  }
  const valueList = Array.from(comparable)
  const valueCondition =
    valueList.length === 0
      ? null
      : {
          field: condition.sourceColumn,
          operator: (valueList.length === 1 ? 'equals' : 'in') as 'equals' | 'in',
          value: valueList.length === 1 ? valueList[0] : valueList,
        }
  const emptyCondition = includeEmpty
    ? {
        field: condition.sourceColumn,
        operator: 'is_empty' as const,
        value: '',
      }
    : null
  if (emptyCondition && valueCondition) {
    return { logic: 'OR', conditions: [emptyCondition, valueCondition] }
  }
  return (
    emptyCondition ??
    valueCondition ?? {
      field: condition.sourceColumn,
      operator: 'in',
      value: [],
    }
  )
}

export function audienceTagConditionsToPopulationFilter(
  conditions: Array<AudienceTagCondition>,
): PopulationFilter {
  return {
    logic: 'AND',
    conditions: conditions.map(conditionToFilterNode),
  }
}

interface AudienceWithIndustryLabels {
  industryLabels?: Array<IndustryLabel> | null
  industryLabel?: IndustryLabel | null
}

export function extractIndustrySelection(audience: AudienceWithIndustryLabels | null | undefined): {
  tokens: Array<string>
  tokenNames: Map<string, string>
} {
  const labels =
    audience?.industryLabels && audience.industryLabels.length > 0
      ? audience.industryLabels
      : audience?.industryLabel
        ? [audience.industryLabel]
        : []

  const tokenNames = new Map<string, string>()
  for (const label of labels) {
    tokenNames.set(label.token, label.name)
  }

  return {
    tokens: labels.map((label) => label.token),
    tokenNames,
  }
}
